import config from '../../config';
import { selectAcceptedDocuments, isSupportingDocument } from '../../library/accepted-documents';
import { getPrimaryDocumentStatus, getServiceDocumentStatus } from '../../library/document-status';
import { getPrimaryDocumentMessage, SUPPORTING_DOCUMENTS_MESSAGE } from '../../library/document-display';
import { setAppNotification } from './app-actions';
import { requestServiceDocumentTypes } from './service-document-actions';

const serviceContext = (state) => ({
  serviceId: state.quote.result?.service?.id || state.remit.remitService?.id,
  businessCode: state.remit.remitProduct?.businessCode,
  countryCode: state.remit.remitRecipient?.country,
});
const contextKey = (state) => JSON.stringify(serviceContext(state));
const serviceMessage = (status) => status.pending ? 'Your required identity document is under review. Please wait for approval.'
  : status.rejected ? 'Your required identity document was rejected. Please provide a replacement.'
  : status.expiredDocument ? 'Your required identity document has expired. Please provide a current document.'
  : status.hasSupporting ? SUPPORTING_DOCUMENTS_MESSAGE : 'Please provide an approved, valid identity document accepted by this service.';

/** Recheck the latest store synchronously, immediately before issuing a payment request. */
export const verifyPaymentDocuments = ({ isPaymentPublic = false, history, serviceTypes, serviceKey } = {}) => (dispatch, getState) => {
  if (isPaymentPublic) return { isPaymentPublic: true };
  const state = getState();
  const accepted = selectAcceptedDocuments(state);
  const primary = getPrimaryDocumentStatus(state.auth.user.document, accepted);
  const fail = (message, types) => {
    dispatch(setAppNotification(message, 'error'));
    history?.push('/account/documents', types?.length ? { serviceIdTypes: types } : undefined);
    return null;
  };
  if (!primary.eligible) return fail(getPrimaryDocumentMessage(primary, accepted));
  if (serviceKey && serviceKey !== contextKey(state)) return fail('Your payment details changed. Please review your selection and try again.');
  const service = serviceTypes ? getServiceDocumentStatus(state.auth.user.document, serviceTypes, accepted) : null;
  if (service && !service.eligible) {
    return fail(serviceMessage(service), serviceTypes.filter((type) => !isSupportingDocument(type.dsscId, accepted)));
  }
  return { document: service?.document || primary.document,
    idType: serviceTypes?.find((type) => type.dsscId === service?.document?.type),
    serviceTypes, serviceKey, history };
};

/**
 * Resolve service requirements for signed-in new and resumed payments, then verify current evidence.
 * Public payment links retain their existing authentication and verification contract.
 */
export const checkPaymentDocuments = (options = {}) => async (dispatch, getState) => {
  const primary = dispatch(verifyPaymentDocuments(options));
  if (!primary || options.isPaymentPublic) return primary;
  const state = getState();
  const { serviceId, businessCode, countryCode } = serviceContext(state);
  const services = (config.REACT_APP_SERVICES_WITH_ID_TYPES || '').split(',').map((id) => id.trim().toUpperCase());
  const serviceKey = contextKey(state);
  if (!serviceId || !services.includes(serviceId.toUpperCase())) return { ...primary, serviceKey };
  if (!businessCode || !countryCode) {
    dispatch(setAppNotification('Unable to check document requirements. Please select a service and country again.', 'error'));
    return null;
  }
  try {
    const serviceTypes = await requestServiceDocumentTypes(businessCode, countryCode).promise;
    return dispatch(verifyPaymentDocuments({ ...options, serviceTypes, serviceKey }));
  } catch {
    dispatch(setAppNotification('Unable to check document requirements. Please try again.', 'error'));
    return null;
  }
};

/** Recheck current primary and service evidence before leaving the calculated-amount page. */
export const verifyQuoteDocuments = ({ serviceTypes, requirementsUnavailable = false } = {}) => (dispatch, getState) => {
  const state = getState();
  const accepted = selectAcceptedDocuments(state);
  const primary = getPrimaryDocumentStatus(state.auth.user.document, accepted);
  const service = serviceTypes ? getServiceDocumentStatus(state.auth.user.document, serviceTypes, accepted) : null;
  const message = !primary.eligible ? getPrimaryDocumentMessage(primary, accepted)
    : requirementsUnavailable || (serviceTypes && !serviceTypes.length) ? 'Unable to check document requirements. Please try again.'
    : service && !service.eligible ? serviceMessage(service) : null;
  if (message) {
    dispatch(setAppNotification(message, 'error'));
    return false;
  }
  return true;
};
